import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { EntryFormDialog } from '@/components/common/EntryFormDialog';
import { useUpdateJob } from '../hooks/useUpdateJob';
import { useEstimators } from '@/features/leads/hooks/useEstimators';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';
import type { JobDetailDto } from '../types';

interface JobEditFormProps {
  job: JobDetailDto;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

export function JobEditForm({ job, isOpen, onClose, onSave }: JobEditFormProps) {
  const { hasPermission } = useAuth();
  const canEditCommission = hasPermission(Permissions.CommissionsUpdate);
  const canViewCommission = hasPermission(Permissions.CommissionsRead);
  const isEditable = job.status === 'Active' || job.status === 'Collections';

  // Track previous state to detect when to reset form (React-recommended pattern)
  const [prevIsOpen, setPrevIsOpen] = useState(false);
  const [prevJobId, setPrevJobId] = useState(job.id);
  const [description, setDescription] = useState(job.description || '');
  const [notes, setNotes] = useState(job.notes || '');
  const [assignedToUserId, setAssignedToUserId] = useState<number | undefined>(job.assignedToUserId ?? undefined);
  const [commissionRate, setCommissionRate] = useState<string>(job.commissionPercent?.toString() ?? '');
  const { updateJobDetails, isUpdating, error } = useUpdateJob();
  const { estimators } = useEstimators();

  // Reset form when dialog opens or job changes (during render, not in effect)
  if ((isOpen && !prevIsOpen) || job.id !== prevJobId) {
    setPrevIsOpen(isOpen);
    setPrevJobId(job.id);
    setDescription(job.description || '');
    setNotes(job.notes || '');
    setAssignedToUserId(job.assignedToUserId ?? undefined);
    setCommissionRate(job.commissionPercent?.toString() ?? '');
  } else if (!isOpen && prevIsOpen) {
    setPrevIsOpen(false);
  }

  const handleEstimatorChange = (value: string) => {
    const newId = value ? Number(value) : undefined;
    setAssignedToUserId(newId);

    // Auto-populate commission rate from estimator's default
    if (newId && canEditCommission) {
      const estimator = estimators.find(e => e.id === newId);
      if (estimator?.commissionRate != null) {
        setCommissionRate(estimator.commissionRate.toString());
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = await updateJobDetails(job.id, {
      description: description || undefined,
      notes: notes || undefined,
      ...(isEditable && {
        assignedToUserId: assignedToUserId ?? null,
        ...(canEditCommission && commissionRate !== '' && {
          commissionRate: Number(commissionRate),
        }),
      }),
    });

    if (result) {
      onSave();
      onClose();
    }
  };

  const handleClose = () => {
    setDescription(job.description || '');
    setNotes(job.notes || '');
    setAssignedToUserId(job.assignedToUserId ?? undefined);
    setCommissionRate(job.commissionPercent?.toString() ?? '');
    onClose();
  };

  return (
    <EntryFormDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Job Details"
      error={error}
      isSubmitting={isUpdating}
      submitLabel="Save Changes"
      editLabel="Save Changes"
      isEditing={true}
      onSubmit={handleSubmit}
      maxWidth="sm:max-w-[600px]"
    >
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter job description..."
          rows={4}
          className="text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter job notes..."
          rows={4}
          className="text-sm"
        />
      </div>

      {isEditable && (
        <>
          <div className="space-y-2">
            <Label htmlFor="assignedTo">Estimator / PM</Label>
            <Select
              id="assignedTo"
              value={assignedToUserId?.toString() ?? ''}
              onChange={(e) => handleEstimatorChange(e.target.value)}
            >
              <option value="">Select estimator</option>
              {estimators.map((est) => (
                <option key={est.id} value={est.id.toString()}>
                  {est.firstName} {est.lastName}
                </option>
              ))}
            </Select>
          </div>

          {canViewCommission && canEditCommission && (
            <div className="space-y-2">
              <Label htmlFor="commissionRate">Commission Rate (%)</Label>
              <Input
                id="commissionRate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
                placeholder="0.00"
                className="text-sm"
              />
            </div>
          )}
        </>
      )}
    </EntryFormDialog>
  );
}
